// src/modules/app-views/invoices/pages/Document/tabs/RecordTab/ValidationAlert.jsx
// *******************************************************************************
// VALIDATION ALERT
// *******************************************************************************
// Displays validation errors and warnings in an alert at the top of RecordTab.
// Only shown when documentValidation exists and has issues.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Alert, Space, Typography } from 'antd';
import { CloseCircleFilled, WarningFilled, InfoCircleOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';

const { Text } = Typography;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Get display message for validation error/warning
 * Priority: 1. message (if descriptive), 2. translated code, 3. generic fallback
 */
function getDisplayMessage(item, isError = true) {
  if (typeof item === 'string') return item;

  const { code, message, details } = item;

  // If we have a descriptive message, use it (priority 1)
  if (message && message.length > 0 && message !== code) {
    return message;
  }

  // Try to translate the code (priority 2)
  if (code) {
    const translated = translateX(code);
    // translateX returns the key itself if no translation found
    // Check if translation exists (different from the code)
    if (translated && translated !== code) {
      return translated;
    }
  }

  // Generic fallback messages based on known error codes (priority 3)
  if (code) {
    switch (code) {
      case 'EMISSION_ERROR':
        return translateX('validation_emission_error_generic');
      case 'TOTALS_MISMATCH':
        return translateX('validation_totals_mismatch_generic');
      case 'INVALID_CHANGE_VALUE':
        return translateX('validation_invalid_change_generic');
      case 'PAYMENT_TOTAL_MISMATCH':
        return translateX('validation_payment_mismatch_generic');
      default:
        // Return code with a hint to validate again
        return `${code} - ${translateX('validation_unknown_error_hint')}`;
    }
  }

  // Ultimate fallback
  return isError
    ? translateX('validation_unknown_error')
    : translateX('validation_unknown_warning');
}

/**
 * Format details object for display (if available)
 */
function formatDetails(details) {
  if (!details || typeof details !== 'object') return null;

  const parts = [];
  if (details.declared !== undefined && details.calculated !== undefined) {
    parts.push(`${translateX('declared')}: ${details.declared}`);
    parts.push(`${translateX('calculated')}: ${details.calculated}`);
    if (details.difference !== undefined) {
      parts.push(`${translateX('difference')}: ${details.difference}`);
    }
  }

  return parts.length > 0 ? parts.join(' | ') : null;
}

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ValidationAlert = ({ data }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  const documentValidation = data?.documentValidation;
  const isValid = documentValidation?.isValid;
  const errors = documentValidation?.errorList || [];
  const warnings = documentValidation?.warningList || [];
  const hasErrors = errors.length > 0;
  const hasWarnings = warnings.length > 0;
  const hasIssues = hasErrors || hasWarnings;

  // ─────────────────────────────────────────────────────────────────────────────
  // UI
  // ─────────────────────────────────────────────────────────────────────────────
  // Don't show if valid or no validation data
  if (!documentValidation || isValid || !hasIssues) return null;

  // Determine alert type based on issues
  const alertType = hasErrors ? 'error' : 'warning';
  const alertTitle = hasErrors
    ? translateX('validation_errors_found')
    : translateX('validation_warnings_found');

  return (
    <Alert
      type={alertType}
      showIcon
      closable
      style={{ marginBottom: 16 }}
      title={
        <Text strong>
          {alertTitle} ({errors.length + warnings.length})
        </Text>
      }
      description={
        <Space orientation="vertical" style={{ width: '100%', marginTop: 8 }} size="small">
          {/* Errors List */}
          {hasErrors && (
            <div>
              {errors.map((error, index) => {
                const code = typeof error === 'object' ? error.code : null;
                const field = typeof error === 'object' ? error.field : null;
                const details = typeof error === 'object' ? error.details : null;
                const displayMessage = getDisplayMessage(error, true);
                const detailsText = formatDetails(details);

                return (
                  <div
                    key={`error-${index}`}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      marginBottom: 8,
                    }}
                  >
                    <CloseCircleFilled
                      style={{ color: '#ff4d4f', marginRight: 8, marginTop: 3, flexShrink: 0 }}
                    />
                    <div style={{ flex: 1 }}>
                      <Text>{displayMessage}</Text>
                      {/* Show code as subtitle if we have a descriptive message */}
                      {code && displayMessage !== code && (
                        <Text
                          type="secondary"
                          style={{ fontSize: 11, display: 'block', marginTop: 2 }}
                        >
                          {translateX('code')}: {code}
                          {field && ` | ${translateX('field')}: ${field}`}
                        </Text>
                      )}
                      {/* Show details if available */}
                      {detailsText && (
                        <div
                          style={{
                            marginTop: 4,
                            padding: '4px 8px',
                            background: 'rgba(255, 77, 79, 0.1)',
                            borderRadius: 4,
                            fontSize: 12,
                          }}
                        >
                          <InfoCircleOutlined style={{ marginRight: 4 }} />
                          {detailsText}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Warnings List */}
          {hasWarnings && (
            <div style={{ marginTop: hasErrors ? 8 : 0 }}>
              {hasErrors && warnings.length > 0 && (
                <Text
                  strong
                  style={{ color: '#faad14', display: 'block', marginBottom: 4, fontSize: 12 }}
                >
                  {translateX('warnings')}:
                </Text>
              )}
              {warnings.map((warning, index) => {
                const code = typeof warning === 'object' ? warning.code : null;
                const field = typeof warning === 'object' ? warning.field : null;
                const details = typeof warning === 'object' ? warning.details : null;
                const displayMessage = getDisplayMessage(warning, false);
                const detailsText = formatDetails(details);

                return (
                  <div
                    key={`warning-${index}`}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      marginBottom: 8,
                    }}
                  >
                    <WarningFilled
                      style={{ color: '#faad14', marginRight: 8, marginTop: 3, flexShrink: 0 }}
                    />
                    <div style={{ flex: 1 }}>
                      <Text>{displayMessage}</Text>
                      {/* Show code as subtitle if we have a descriptive message */}
                      {code && displayMessage !== code && (
                        <Text
                          type="secondary"
                          style={{ fontSize: 11, display: 'block', marginTop: 2 }}
                        >
                          {translateX('code')}: {code}
                          {field && ` | ${translateX('field')}: ${field}`}
                        </Text>
                      )}
                      {/* Show details if available */}
                      {detailsText && (
                        <div
                          style={{
                            marginTop: 4,
                            padding: '4px 8px',
                            background: 'rgba(250, 173, 20, 0.1)',
                            borderRadius: 4,
                            fontSize: 12,
                          }}
                        >
                          <InfoCircleOutlined style={{ marginRight: 4 }} />
                          {detailsText}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Space>
      }
    />
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ValidationAlert;
